/**
 * "Einspringen" (Kapitel 11) — Mitarbeiter trägt eine Schicht KOMPLETT NEU
 * und selbst ein, an einem beliebigen Standort der eigenen Firma (auch
 * einem, an dem er organisatorisch nicht zugeordnet ist). Der reale Fall:
 * der Chef ruft spontan an und fragt, ob jemand einspringen kann.
 *
 * Bewusst KEIN Outbox/Offline-Eintrag wie die übrigen Schicht-Mutationen —
 * die serverseitigen Prüfungen (genehmigte Abwesenheit, Doppelbuchung) und
 * der Override-Dialog brauchen eine Live-Antwort vom Server, keine später
 * beim Sync auftauchende Fehlermeldung. Nach Erfolg wird das Ergebnis direkt
 * in den lokalen Cache geschrieben, damit Kalender/Übersicht sofort
 * aktualisiert sind.
 */

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useAuth } from '../store/auth';
import { useLocationsWithTemplates } from '../data/locations';
import type { LocalLocationWithTemplates } from '../data/locations';
import type { LocalTemplate, LocalShift } from '../db/schema';
import { db } from '../db/schema';
import { shiftsApi } from '../lib/shifts';
import { ApiError } from '../lib/api';
import { shiftDurationHours, shiftHours, shiftWage } from '../lib/payroll';
import { formatMoney } from '../lib/format';
import { hapticError, hapticSuccess } from '../lib/haptics';
import { ChipButton } from './ShiftFormModal';

interface Props {
  defaultDate?: string;
  onClose: () => void;
  onSaved: (shift: LocalShift) => void;
}

// Gesamtprüfung 11.09.2026: lokales Datum statt UTC (sonst nach Mitternacht bis 01/02 Uhr noch „gestern“).
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function defaultStatusForDate(iso: string): 'planned' | 'done' {
  return iso < todayISO() ? 'done' : 'planned';
}

function genClientUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `tmp-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

export function EinspringenModal({ defaultDate, onClose, onSaved }: Props) {
  const me = useAuth((s) => s.user)!;
  const token = useAuth((s) => s.token)!;
  const allLocations = useLocationsWithTemplates();

  // Kapitel 11.4: jeder Standort der eigenen Firma ist wählbar — eigene
  // Standorte (LocationMembership, egal ob Mitarbeiter oder Filialleiter)
  // zuerst, der Rest danach klar als "nicht zugeordnet" markiert.
  const ownLocationIds = useMemo(
    () => new Set(me.locationMemberships.map((m) => m.locationId)),
    [me.locationMemberships],
  );
  const locations = useMemo(() => {
    if (!allLocations) return undefined;
    const active = allLocations.filter((l) => l.isActive);
    const mine = active.filter((l) => ownLocationIds.has(l.id));
    const foreign = active.filter((l) => !ownLocationIds.has(l.id));
    return [...mine, ...foreign];
  }, [allLocations, ownLocationIds]);

  const [shiftDate, setShiftDate] = useState(defaultDate ?? todayISO());
  const [locationId, setLocationId] = useState('');
  const [templateId, setTemplateId] = useState<string | ''>('');
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('14:00');
  const [isOpenEnd, setIsOpenEnd] = useState(false);
  const [note, setNote] = useState('');
  const [showBonus, setShowBonus] = useState(false);
  const [bonusInput, setBonusInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Kapitel 5.2/11.7: Server meldet genehmigten Urlaub/Krank am gewählten
  // Tag per 409 — dann diesen Bestätigungs-Dialog einblenden.
  const [absenceConflict, setAbsenceConflict] = useState<string | null>(null);

  useEffect(() => {
    if (locationId || !locations || locations.length === 0) return;
    setLocationId(locations[0]!.id);
  }, [locations, locationId]);

  const selectedLocation = useMemo<LocalLocationWithTemplates | undefined>(
    () => locations?.find((l) => l.id === locationId),
    [locations, locationId],
  );
  const isForeign = !!selectedLocation && !ownLocationIds.has(selectedLocation.id);

  const availableTemplates = useMemo<LocalTemplate[]>(
    () => (selectedLocation?.templates ?? []).filter((t) => t.isActive),
    [selectedLocation],
  );

  useEffect(() => {
    if (templateId && !availableTemplates.some((t) => t.id === templateId)) setTemplateId('');
  }, [availableTemplates, templateId]);

  function applyTemplate(id: string) {
    setTemplateId(id);
    if (!id) {
      setIsOpenEnd(false);
      return;
    }
    const tpl = availableTemplates.find((t) => t.id === id);
    if (tpl) {
      setStartTime(tpl.startTime);
      setEndTime(tpl.endTime);
      setIsOpenEnd(tpl.isOpenEnd);
    }
  }

  const parsedBonus = (() => {
    const n = Number(bonusInput.replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? n : 0;
  })();
  const liveHours = shiftHours({ startTime, endTime, bonusHours: parsedBonus, isOpenEnd });
  const liveWage = shiftWage({
    shiftDate,
    startTime,
    endTime,
    bonusHours: 0, // Zusatzstunden sind erst final, wenn der Chef bestätigt hat
    isOpenEnd,
    hourlyWage: me.hourlyWage ?? 0,
    status: defaultStatusForDate(shiftDate),
  });
  const openEndPending = isOpenEnd && startTime === endTime;

  async function submit(vacationOverride: boolean) {
    setError(null);
    setBusy(true);
    try {
      const saved = await shiftsApi.create(token, {
        locationId,
        templateId: templateId || null,
        shiftDate,
        startTime,
        endTime,
        note: note.trim() ? note.trim() : null,
        isOpenEnd,
        requestedBonusHours: parsedBonus > 0 ? parsedBonus : undefined,
        vacationOverride: vacationOverride || undefined,
        clientUuid: genClientUuid(),
      });
      await db.shifts.put({ ...saved, pending: false });
      hapticSuccess();
      setAbsenceConflict(null);
      onSaved(saved);
    } catch (err) {
      hapticError();
      if (err instanceof ApiError && err.status === 409) {
        const details = err.body as { details?: { code?: string } } | null;
        if (details?.details?.code === 'APPROVED_ABSENCE_CONFLICT') {
          setAbsenceConflict(err.message);
          setBusy(false);
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Einspringen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!locationId) {
      setError('Bitte einen Standort wählen.');
      hapticError();
      return;
    }
    if (!isOpenEnd && startTime === endTime) {
      setError('Start- und End-Zeit dürfen nicht identisch sein.');
      hapticError();
      return;
    }
    await submit(false);
  }

  const loadingLocations = locations === undefined;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-surface-2 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-md lg:max-w-lg max-h-[92vh] sm:max-h-[90vh] overflow-y-auto border border-border/60">
        <h3 className="text-lg font-semibold mb-1">Einspringen</h3>
        <p className="text-xs text-slate-400 mb-4">
          Der Chef hat spontan angerufen? Trag die Schicht direkt selbst ein — an jedem
          Standort deiner Firma, auch wenn du dort nicht fest zugeordnet bist.
        </p>

        {absenceConflict ? (
          <div className="space-y-3">
            <div className="bg-amber-900/30 border border-amber-700/50 rounded p-3 text-sm text-amber-200">
              {absenceConflict} Der Chef wird automatisch informiert.
            </div>
            {error && <p className="text-sm text-red-400">{error}</p>}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button type="button" onClick={() => setAbsenceConflict(null)} className="btn-ghost w-full sm:w-auto">
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => submit(true)}
                disabled={busy}
                className="btn w-full sm:w-auto"
              >
                {busy ? 'Speichere…' : 'Trotzdem eintragen'}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <label className="block">
              <span className="block text-sm text-slate-300 mb-1">Datum</span>
              <input
                required
                type="date"
                value={shiftDate}
                onChange={(e) => setShiftDate(e.target.value)}
                className="input"
              />
            </label>

            <label className="block">
              <span className="block text-sm text-slate-300 mb-1">Standort</span>
              <select
                required
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                className="input"
              >
                <option value="" disabled>
                  {loadingLocations ? '— lädt … —' : '— wählen —'}
                </option>
                {locations?.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                    {!ownLocationIds.has(l.id) ? ' (nicht zugeordnet)' : ''}
                  </option>
                ))}
              </select>
              {isForeign && (
                <p className="text-xs text-amber-300 mt-1">
                  Du bist hier nicht fest zugeordnet — das ist kein Problem, der Chef wird
                  automatisch informiert.
                </p>
              )}
            </label>

            {availableTemplates.length > 0 && (
              <div>
                <span className="block text-sm text-slate-300 mb-1.5">Vorlage</span>
                <div className="flex flex-wrap gap-2">
                  <ChipButton active={!templateId} onClick={() => applyTemplate('')}>
                    freie Zeiten
                  </ChipButton>
                  {availableTemplates.map((t) => (
                    <ChipButton
                      key={t.id}
                      active={templateId === t.id}
                      onClick={() => applyTemplate(t.id)}
                      title={`${t.startTime}–${t.endTime}${t.isOpenEnd ? ' (offen)' : ''}`}
                    >
                      {t.name}
                    </ChipButton>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-sm text-slate-300 mb-1">Start</span>
                <input
                  required
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="input"
                />
              </label>
              <label className="block">
                <span className="block text-sm text-slate-300 mb-1">
                  {isOpenEnd ? 'Ende (optional)' : 'Ende'}
                </span>
                <input
                  required={!isOpenEnd}
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="input"
                />
              </label>
            </div>

            {openEndPending && (
              <p className="text-xs text-amber-300">
                Endzeit fehlt — Schicht zählt vorläufig mit 0 h. Trag sie ein, sobald du
                Feierabend hast.
              </p>
            )}

            <div className="bg-slate-900/50 rounded p-3 text-sm space-y-0.5 border border-border/40">
              <div className="flex justify-between">
                <span className="text-slate-400">Schicht-Zeit</span>
                <span className="font-medium">
                  {openEndPending
                    ? '— (offen)'
                    : `${shiftDurationHours({ startTime, endTime, isOpenEnd }).toFixed(2).replace('.', ',')} h`}
                </span>
              </div>
              {parsedBonus > 0 && (
                <div className="flex justify-between text-amber-300">
                  <span>Zusatzstunden (beantragt)</span>
                  <span className="font-medium">+{parsedBonus.toFixed(2).replace('.', ',')} h</span>
                </div>
              )}
              <div className="flex justify-between border-t border-border/50 pt-1 mt-1">
                <span className="text-slate-400">Bezahlte Stunden (ohne Zusatzstunden)</span>
                <span className="font-medium">{liveHours.toFixed(2).replace('.', ',')} h</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Lohn (brutto, ohne Zusatzstunden)</span>
                <span className="font-medium text-emerald-400">{formatMoney(liveWage)}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowBonus((v) => !v)}
              className="text-sm text-brand-light hover:text-brand !min-h-0 inline-flex items-center gap-1"
              aria-expanded={showBonus}
            >
              <span>{showBonus ? '▾' : '▸'}</span> Zusatzstunden angeben
            </button>

            {showBonus && (
              <div className="space-y-2 pt-1">
                <label className="block">
                  <span className="block text-sm text-slate-300 mb-1">
                    Zusatzstunden (z. B. "schreib dir ruhig eine Stunde mehr auf")
                  </span>
                  <input
                    inputMode="decimal"
                    value={bonusInput}
                    onChange={(e) => setBonusInput(e.target.value)}
                    className="input"
                    placeholder="0"
                  />
                </label>
                <p className="text-[11px] text-slate-500">
                  Nur der Chef kann Zusatzstunden bestätigen oder ablehnen — bis dahin gilt die
                  Schicht als „unbestätigt", zählt aber bereits normal im Kalender.
                </p>
              </div>
            )}

            <label className="block">
              <span className="block text-sm text-slate-300 mb-1">Notiz</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="input resize-y"
                placeholder="Optional"
              />
            </label>

            {error && <p className="text-sm text-red-400">{error}</p>}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="btn-ghost w-full sm:w-auto">
                Abbrechen
              </button>
              <button type="submit" disabled={busy || !locationId} className="btn w-full sm:w-auto">
                {busy ? 'Speichere…' : 'Einspringen speichern'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
